import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  PurchaseOrderStatus,
  SupplierActiveFilter,
  type PurchaseOrderDetail,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { useUnsavedChangesGuard } from '@/modules/medical/useUnsavedChangesGuard';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCreatePurchaseOrderMutation,
  usePurchaseOrderQuery,
  useUpdatePurchaseOrderMutation,
} from '../api/purchase-orders.api';
import { useSuppliersQuery } from '../api/suppliers.api';
import { taxProfileLabel, usePurchasingTaxProfilesQuery } from '../api/tax-profiles.api';
import { VariantPicker, type PickedVariant } from '../components/VariantPicker';

/** A line as the screen holds it: the quantity and the cost stay the text the buyer typed until they are parsed. */
interface EditorLine {
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  quantityInput: string;
  costInput: string;
  /** Empty until the profiles arrive, which is when a new line takes the tenant's default. */
  taxProfileId: string;
}

const asQuantity = (value: string): number => {
  const parsed = Number(value.replace(/[^\d]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

function linesOf(order: PurchaseOrderDetail | undefined, toInput: (minor: number) => string) {
  return (order?.lines ?? []).map<EditorLine>((line) => ({
    variantId: line.variantId,
    sku: line.sku,
    productName: line.productName,
    variantName: line.variantName,
    quantityInput: String(line.quantityOrdered),
    costInput: toInput(line.agreedUnitCostMinor),
    taxProfileId: line.taxProfileId ?? '',
  }));
}

/**
 * Writing a purchase order, before anyone commits to it.
 *
 * A draft lives on the server, not in this page: leaving and coming back reads it again rather than
 * restoring something the browser kept, which is what stops two tabs disagreeing about what was
 * ordered. Nothing commercial is stored locally.
 *
 * Totals shown while typing are a preview and say so. What the document is worth is whatever the
 * server answers with after a save — a purchase order's value is not a sum this screen is entitled
 * to decide.
 */
export function PurchaseOrderEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { activeBranch, branches, selectBranch } = useBranch();
  const { format, parse, toInput } = useCurrency();
  const { locale } = useLocale();

  const isNew = !orderId;
  const existing = usePurchaseOrderQuery(orderId);
  const order = existing.data;

  const [supplierId, setSupplierId] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [supplierReference, setSupplierReference] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<EditorLine[]>([]);
  const [isPicking, setIsPicking] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [duplicateSku, setDuplicateSku] = useState<string | null>(null);

  const suppliers = useSuppliersQuery({ pageSize: 100, active: SupplierActiveFilter.ACTIVE });
  const taxProfiles = usePurchasingTaxProfilesQuery();
  const defaultTaxProfileId =
    taxProfiles.data?.find((profile) => profile.isDefault)?.id ?? taxProfiles.data?.[0]?.id ?? '';
  const create = useCreatePurchaseOrderMutation();
  const update = useUpdatePurchaseOrderMutation();
  const guard = useUnsavedChangesGuard(isDirty);

  // The server's draft is the one being edited, so the form is seeded from it rather than from anything kept here.
  useEffect(() => {
    if (!order) return;
    setSupplierId(order.supplierId);
    setExpectedDeliveryDate(order.expectedDeliveryDate ?? '');
    setSupplierReference(order.supplierReference ?? '');
    setNotes(order.notes ?? '');
    setLines(linesOf(order, toInput));
    setIsDirty(false);
  }, [order, toInput]);

  const touch = () => setIsDirty(true);

  const preview = useMemo(() => {
    const rateOf = (taxProfileId: string) =>
      taxProfiles.data?.find((profile) => profile.id === taxProfileId)?.rateBasisPoints ?? 0;

    return lines.reduce((total, line) => {
      const net = asQuantity(line.quantityInput) * (parse(line.costInput) ?? 0);
      // A purchase cost is agreed before tax, so the tax is added on top — never extracted.
      return total + net + Math.round((net * rateOf(line.taxProfileId)) / 10_000);
    }, 0);
  }, [lines, parse, taxProfiles.data]);

  const isEditable = isNew || order?.status === PurchaseOrderStatus.DRAFT;
  const isSaving = create.isPending || update.isPending;
  const saveError = apiErrorMessage(create.error) ?? apiErrorMessage(update.error);

  const addLine = (picked: PickedVariant) => {
    if (lines.some((line) => line.variantId === picked.variantId)) {
      setDuplicateSku(picked.sku);
      setIsPicking(false);
      return;
    }

    setLines((current) => [
      ...current,
      {
        variantId: picked.variantId,
        sku: picked.sku,
        productName: picked.productName,
        variantName: picked.variantName,
        quantityInput: '1',
        costInput: toInput(picked.costPriceMinor ?? 0),
        taxProfileId: defaultTaxProfileId,
      },
    ]);
    setDuplicateSku(null);
    setIsPicking(false);
    touch();
  };

  const setLine = (index: number, patch: Partial<EditorLine>) => {
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
    touch();
  };

  const removeLine = (index: number) => {
    setLines((current) => current.filter((_, position) => position !== index));
    touch();
  };

  const payloadLines = () =>
    lines.map((line) => ({
      variantId: line.variantId,
      quantity: asQuantity(line.quantityInput),
      agreedUnitCostMinor: parse(line.costInput) ?? 0,
      ...(line.taxProfileId ? { taxProfileId: line.taxProfileId } : {}),
    }));

  const header = () => ({
    ...(expectedDeliveryDate ? { expectedDeliveryDate } : {}),
    ...(supplierReference.trim() ? { supplierReference: supplierReference.trim() } : {}),
    ...(notes.trim() ? { notes: notes.trim() } : {}),
  });

  const save = () => {
    if (!supplierId) return;

    if (isNew) {
      create.mutate(
        { supplierId, lines: payloadLines(), ...header() },
        {
          onSuccess: (saved) => {
            setIsDirty(false);
            guard.allowNextNavigation();
            void navigate(`/purchasing/orders/${saved.id}`);
          },
        },
      );
      return;
    }

    if (!order) return;
    update.mutate(
      {
        id: order.id,
        version: order.version,
        supplierId,
        lines: payloadLines(),
        ...header(),
      },
      {
        onSuccess: (saved) => {
          setIsDirty(false);
          guard.allowNextNavigation();
          void navigate(`/purchasing/orders/${saved.id}`);
        },
      },
    );
  };

  if (!isNew && existing.isPending) return <LoadingState />;
  if (!isNew && (existing.isError || !order)) {
    return (
      <Alert tone="error">
        {apiErrorMessage(existing.error) ?? t('purchasing.orders.loadError')}
      </Alert>
    );
  }

  // A purchase order is placed at a branch and received there: with none chosen there is nothing to create it against.
  if (isNew && !activeBranch) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('purchasing.orders.create')} />
        <Alert tone="warning">{t('purchasing.orders.branchRequired')}</Alert>
        <Card>
          <CardBody className="space-y-3">
            <p className="text-muted text-sm">{t('purchasing.orders.branchRequiredHint')}</p>
            <Select
              aria-label={t('purchasing.orders.fields.branch')}
              value=""
              options={[
                { value: '', label: t('purchasing.orders.chooseBranch') },
                ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
              ]}
              onChange={(event) => {
                if (event.target.value) selectBranch(event.target.value);
              }}
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!isEditable && order) {
    return (
      <div className="space-y-4">
        <Alert tone="warning">{t('purchasing.orders.notDraft')}</Alert>
        <Button variant="secondary" onClick={() => void navigate(`/purchasing/orders/${order.id}`)}>
          {t('purchasing.orders.backToOrder')}
        </Button>
      </div>
    );
  }

  const branchName = order?.branchName ?? activeBranch?.name ?? '';

  return (
    <div className="space-y-6">
      <PageHeader
        title={isNew ? t('purchasing.orders.create') : t('purchasing.orders.editDraft')}
        subtitle={order?.reference ?? t('purchasing.orders.referenceOnSave')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(order ? `/purchasing/orders/${order.id}` : '/purchasing/orders')
              }
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={save}
              isLoading={isSaving}
              disabled={!supplierId || lines.some((line) => asQuantity(line.quantityInput) < 1)}
            >
              {isNew ? t('purchasing.orders.saveDraft') : t('common.save')}
            </Button>
          </div>
        }
      />

      {saveError ? <Alert tone="error">{saveError}</Alert> : null}
      {duplicateSku ? (
        <Alert tone="warning">{t('purchasing.orders.duplicateLine', { sku: duplicateSku })}</Alert>
      ) : null}

      <Card>
        <CardHeader title={t('purchasing.orders.headerSection')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('purchasing.orders.fields.supplier')} required>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={supplierId}
                options={[
                  { value: '', label: t('purchasing.orders.chooseSupplier') },
                  ...(suppliers.data?.items ?? []).map((supplier) => ({
                    value: supplier.id,
                    label: supplier.name,
                  })),
                ]}
                onChange={(event) => {
                  setSupplierId(event.target.value);
                  touch();
                }}
              />
            )}
          </FormField>

          <div className="space-y-1">
            <span className="field-label">{t('purchasing.orders.fields.branch')}</span>
            <p className="text-ink text-sm">{branchName}</p>
            <p className="text-muted text-xs">{t('purchasing.orders.branchFixed')}</p>
          </div>

          <Input
            type="date"
            label={t('purchasing.orders.fields.expectedDeliveryDate')}
            value={expectedDeliveryDate}
            onChange={(event) => {
              setExpectedDeliveryDate(event.target.value);
              touch();
            }}
          />
          <Input
            label={t('purchasing.orders.fields.supplierReference')}
            value={supplierReference}
            onChange={(event) => {
              setSupplierReference(event.target.value);
              touch();
            }}
          />

          <div className="sm:col-span-2">
            <label className="field-label" htmlFor="purchase-order-notes">
              {t('purchasing.orders.fields.notes')}
            </label>
            <textarea
              id="purchase-order-notes"
              rows={2}
              dir="auto"
              className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4 focus:outline-none"
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
                touch();
              }}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.orders.linesSection')}
          subtitle={t('purchasing.orders.taxExclusive')}
          action={
            <Button variant="secondary" size="sm" onClick={() => setIsPicking(true)}>
              <Plus className="size-4" aria-hidden />
              {t('purchasing.orders.addLine')}
            </Button>
          }
        />
        <CardBody className="space-y-3">
          {lines.length === 0 ? (
            <p className="text-muted text-sm">{t('purchasing.orders.noLines')}</p>
          ) : (
            <ul className="space-y-3">
              {lines.map((line, index) => (
                <li
                  key={line.variantId}
                  className="border-line grid gap-3 rounded-lg border p-3 sm:grid-cols-[1fr_auto_auto_auto_auto] sm:items-end"
                >
                  <div className="min-w-0">
                    <p className="text-ink text-sm font-medium" dir="auto">
                      {line.variantName ?? line.productName}
                    </p>
                    <p className="text-muted font-mono text-xs" dir="ltr">
                      {line.sku}
                    </p>
                  </div>

                  <Input
                    label={t('purchasing.orders.fields.quantity')}
                    className="sm:w-24"
                    dir="ltr"
                    inputMode="numeric"
                    value={line.quantityInput}
                    aria-label={t('purchasing.orders.quantityFor', { sku: line.sku })}
                    onChange={(event) =>
                      setLine(index, { quantityInput: event.target.value.replace(/[^\d]/g, '') })
                    }
                  />

                  <Input
                    label={t('purchasing.orders.fields.agreedUnitCost')}
                    className="sm:w-32"
                    dir="ltr"
                    inputMode="decimal"
                    value={line.costInput}
                    aria-label={t('purchasing.orders.costFor', { sku: line.sku })}
                    onChange={(event) => setLine(index, { costInput: event.target.value })}
                  />

                  <Select
                    className="sm:w-48"
                    value={line.taxProfileId}
                    aria-label={t('purchasing.orders.taxFor', { sku: line.sku })}
                    options={(taxProfiles.data ?? []).map((profile) => ({
                      value: profile.id,
                      label: taxProfileLabel(profile, locale, (treatment) =>
                        t(`sales.taxTreatments.${treatment}`),
                      ),
                    }))}
                    onChange={(event) => setLine(index, { taxProfileId: event.target.value })}
                  />

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(index)}
                    aria-label={t('purchasing.orders.removeLine', { sku: line.sku })}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="border-line flex items-center justify-between border-t pt-3 text-sm">
            <span className="text-muted">{t('purchasing.orders.previewTotal')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(preview)}
            </span>
          </div>
          <p className="text-muted text-xs">{t('purchasing.orders.previewHint')}</p>
        </CardBody>
      </Card>

      <VariantPicker
        isOpen={isPicking}
        onClose={() => setIsPicking(false)}
        onPick={addLine}
        chosenVariantIds={lines.map((line) => line.variantId)}
      />

      <ConfirmDialog
        isOpen={guard.isBlocked}
        onCancel={guard.stay}
        onConfirm={guard.leave}
        title={t('purchasing.orders.leaveTitle')}
        message={t('purchasing.orders.leaveMessage')}
        confirmLabel={t('purchasing.orders.leaveConfirm')}
        isDestructive
      />
    </div>
  );
}
